import AppLayout from "@/components/AppLayout";
import { CommercialPipeline } from "@/components/dashboard/CommercialPipeline";
import { UrgentTasksList } from "@/components/dashboard/UrgentTasksList";
import { ModuleCard, ModuleConfig } from "@/components/dashboard/ModuleCard";
import { NewsSection } from "@/components/dashboard/NewsSection";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { SectionErrorBoundary } from "@/components/shared/SectionErrorBoundary";
import { geralService, getGeralCrmClientFunnelStageValue } from "@/services/modules/geral";
import { authService } from "@/services/auth";
import { useAIPanel } from "@/contexts/AIPanelContext";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Globe, Lightbulb, Loader2, PenTool, Stamp, AlertCircle, AlarmClock, TrendingUp } from "lucide-react";

export default function DashboardGeralV2() {
  const user = authService.getUser();
  const { openPanel } = useAIPanel();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard", "geral"],
    queryFn: geralService.getDashboard,
  });

  const dashboard = data ?? geralService.getFallback();
  const moduleConfigs: ModuleConfig[] = (dashboard.moduleCards || []).map((item) => ({
    ...item,
    icon:
      item.id === "marcas"
        ? Stamp
        : item.id === "patentes"
        ? Lightbulb
        : item.id === "di"
        ? PenTool
        : Globe,
  }));

  const urgentTasksCount = dashboard.calendarEvents?.length || 0;
  const stalledPipelineCount = getGeralCrmClientFunnelStageValue(dashboard.pipeline, "em-negociacao");

  // First-run (tenant recém-criado): nenhum módulo com atividade e nenhum prazo.
  // Mostra banner de orientação com CTAs reais em vez de um dashboard só de zeros.
  const totalModuleActivity = moduleConfigs.reduce(
    (sum, c) => sum + (c.activeCount ?? 0) + (c.clientCount ?? 0),
    0,
  );
  const isWorkspaceEmpty =
    !isLoading && !isError && totalModuleActivity === 0 && urgentTasksCount === 0;

  const firstName = user?.name?.split(" ")[0] || "Equipe";

  // Hour-based greeting
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";

  return (
    <AppLayout>
      <div className="space-y-7 animate-fade-in">
        <Breadcrumbs items={[{ label: "Página Inicial" }]} />

        {/* ── Command Center Greeting ── */}
        <div
          className="relative flex items-start justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_14px_34px_rgba(27,58,92,0.07)]"
        >
          {/* left accent bar */}
          <div className="absolute top-0 left-0 w-1 h-full bg-slate-900 rounded-l-2xl" />

          <div className="pl-2">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              {greet}, {firstName}.
            </h1>
            <p className="mt-2 flex flex-wrap items-center gap-x-1 gap-y-1 text-[14px] font-medium leading-6 text-slate-600">
              Você tem{" "}
              {/* ── Prazos críticos badge — clicável ── */}
              <button
                onClick={() =>
                  openPanel({
                    mode: "copilot",
                    prompt: `Liste todos os prazos críticos de hoje do escritório, com módulo, processo e ação necessária, em ordem de urgência.`,
                    panelContext: {
                      screen: "Dashboard Geral",
                      entityType: "overview",
                      intentLabel: "Priorizar prazos críticos",
                      metrics: [
                        { label: "Prazos críticos", value: urgentTasksCount, tone: "danger" },
                        { label: "Atendimentos travados", value: stalledPipelineCount, tone: "warning" },
                      ],
                      alerts: [
                        { label: "Hoje", value: `${urgentTasksCount} itens pedem ação`, tone: "danger" },
                      ],
                      allowedActions: ["navigate", "ask"],
                    },
                  })
                }
                className="group inline-flex items-center gap-1 font-bold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 text-[13px] hover:bg-red-100 hover:shadow-sm transition-all duration-150 cursor-pointer"
                title="Ver prazos críticos na IA"
              >
                <AlarmClock className="w-3.5 h-3.5 group-hover:animate-pulse" />
                {urgentTasksCount} prazos críticos
              </button>
              hoje e{" "}
              {/* ── Atendimentos travados badge — clicável ── */}
              <button
                onClick={() =>
                  openPanel({
                    mode: "copilot",
                    prompt: `Mostre os atendimentos comerciais em negociação que estão travados, com o nome do cliente, o responsável e o valor estimado. Sugira próximo passo para destravar cada um.`,
                    panelContext: {
                      screen: "Dashboard Geral",
                      entityType: "pipeline",
                      intentLabel: "Destravar negociações",
                      metrics: [
                        { label: "Atendimentos travados", value: stalledPipelineCount, tone: "warning" },
                        { label: "Prazos críticos", value: urgentTasksCount, tone: "danger" },
                      ],
                      alerts: [
                        { label: "Negociação", value: `${stalledPipelineCount} itens parados`, tone: "warning" },
                      ],
                      allowedActions: ["navigate", "ask"],
                    },
                  })
                }
                className="group inline-flex items-center gap-1 font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[13px] hover:bg-amber-100 hover:shadow-sm transition-all duration-150 cursor-pointer"
                title="Ver atendimentos travados na IA"
              >
                <TrendingUp className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                {stalledPipelineCount} atendimentos travados
              </button>
              .
            </p>
          </div>

          <StatusBadge variant={isError ? "danger" : "success"} size="md">
            {isLoading ? "Sincronizando..." : isError ? "Erro de Conexão" : dashboard.statusLabel}
          </StatusBadge>
        </div>

        {isWorkspaceEmpty && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900">
                  Comece por aqui — seu workspace ainda está vazio
                </h2>
                <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-slate-500">
                  Nenhum processo, cliente ou atendimento cadastrado ainda.
                  Adicione o primeiro processo (ou importe do INPI) para começar
                  a monitorar prazos, exigências e alertas.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <Link
                  to="/patentes/nip-search"
                  className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Buscar no INPI
                </Link>
                <Link
                  to="/comercial/atendimentos"
                  className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-4 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Abrir Comercial
                </Link>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            <span className="ml-3 text-sm font-medium text-slate-500">
              Sincronizando dados operativos...
            </span>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 text-center border border-red-100 bg-red-50/50 rounded-2xl">
            <AlertCircle className="h-10 w-10 text-red-500/60 mb-3" />
            <p className="text-sm font-bold text-red-900">Falha ao sincronizar o dashboard</p>
            <p className="text-xs text-red-600 mt-1">Verifique sua conexão e tente novamente.</p>
          </div>
        ) : (
          <>
            {/* Pipeline comercial — always shown, graceful empty state */}
            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-between px-1">
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Página Inicial CRM</h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Visão macro de atendimento, negociação e fechamento.
                  </p>
                </div>
                <button
                  onClick={() =>
                    openPanel({
                      mode: "copilot",
                      prompt: "Explique o estado atual do pipeline comercial no funil de atendimento, negociação, negócios fechados e negócios perdidos, e qual ação operacional faz mais sentido agora.",
                      panelContext: {
                        screen: "Dashboard Geral",
                        entityType: "pipeline",
                        intentLabel: "Ler pipeline comercial",
                        metrics: [
                          { label: "Prazos críticos", value: urgentTasksCount, tone: "danger" },
                          { label: "Travados", value: stalledPipelineCount, tone: "warning" },
                        ],
                        allowedActions: ["navigate", "ask"],
                      },
                    })
                  }
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Explicar na IA
                </button>
              </div>
              <SectionErrorBoundary title="Pipeline comercial indisponivel">
                <CommercialPipeline data={dashboard.pipeline} />
              </SectionErrorBoundary>
            </div>

            {/* Module cards */}
            <div className="pt-2">
              <h2 className="text-sm font-bold text-slate-900 mb-4 px-1">Visão Geral de Módulos</h2>
              <SectionErrorBoundary title="Visao de modulos indisponivel">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {moduleConfigs.map((config) => (
                    <ModuleCard key={config.id} config={config} />
                  ))}
                </div>
              </SectionErrorBoundary>
            </div>

            <div className="grid grid-cols-12 gap-6 pt-2">
              <div className="col-span-12 lg:col-span-4">
                <SectionErrorBoundary title="Lista de prazos indisponivel">
                  <UrgentTasksList eventsData={dashboard.calendarEvents} onAskAI={openPanel} />
                </SectionErrorBoundary>
              </div>
              <div className="col-span-12 lg:col-span-8">
                <SectionErrorBoundary title="Noticias e comunicados indisponiveis">
                  <NewsSection
                    noticiasData={dashboard.noticias}
                    comunicadosData={dashboard.comunicados}
                  />
                </SectionErrorBoundary>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
